import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, ReplaySubject, Subscription, filter, map } from "rxjs";
import { EDeviceCategory } from "src/app/conf/device-categories";
import { VIRTUAL_ICONS_VALUE } from "src/app/conf/device-icons";
import { EDeviceModel } from "src/app/conf/device-models";
import { Device } from "src/app/model/device";
import { DeviceInfo } from "src/app/model/device-info";
import { DeviceModel } from "src/app/model/device-model";
import { AppState } from "src/app/store/app.reducer";
import * as SiteActions from 'src/app/store/site/site.actions';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { selectAddedDeviceId, selectAddingDevice, selectDeviceModels } from "src/app/store/device/device.selector";
import { CustomValidators } from "src/app/validators/custom.validators";
import { IOPayload } from "src/app/model/io-payload";
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";


@Component({
	templateUrl: './add-device.dialog.component.html',
})

export class AddDeviceDialogComponent implements OnInit, OnDestroy {
	addDeviceForm: FormGroup;

	icons = VIRTUAL_ICONS_VALUE;

	modelFilterCtrl = new FormControl('');
	filteredModels$: ReplaySubject<DeviceModel[]> = new ReplaySubject<DeviceModel[]>(1);

	addingDevice$: Observable<boolean | undefined>;
	periphNumbers: number[] = [];

	model: DeviceModel;

	models: DeviceModel[];
	models$: Observable<DeviceModel[] | undefined>;
	modelsSub: Subscription;

	filteredModels: DeviceModel[];

	addedDeviceId$: Observable<number | undefined>;
	addSub: Subscription;

	parentModel: DeviceModel;
	parentDevice: Device;
	parentInfo: DeviceInfo;
	automationsCount: number;
	subGatewaysCount: number;

	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		if (config.data !== undefined) {
			if (config.data.parentModel !== undefined && config.data.parentModel !== null) {
				this.parentModel = config.data.parentModel;
			} else {
				this.parentModel = new DeviceModel;
			}

			if (config.data.parentDevice !== undefined && config.data.parentDevice !== null) {
				this.parentDevice = config.data.parentDevice;
			} else {
				this.parentDevice = new Device;
			}

			if (config.data.parentInfo !== undefined && config.data.parentInfo !== null) {
				this.parentInfo = config.data.parentInfo;
			} else {
				this.parentInfo = new DeviceInfo;
			}

			if (config.data.automationsCount !== undefined && config.data.automationsCount !== null) {
				this.automationsCount = config.data.automationsCount;
			} else {
				this.automationsCount = 0;
			}

			if (config.data.subGatewaysCount !== undefined && config.data.subGatewaysCount !== null) {
				this.subGatewaysCount = config.data.subGatewaysCount;
			} else {
				this.subGatewaysCount = 0;
			}
		}

		this.initSelectors();
	}

	initSelectors(): void {
		this.models$ = this.store.select(selectDeviceModels);
		this.addingDevice$ = this.store.select(selectAddingDevice);
		this.addedDeviceId$ = this.store.select(selectAddedDeviceId);
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	buildForm(): void {
		const inputs = [];
		if (this.parentInfo.AllowedInputs !== undefined && this.parentInfo.AllowedInputs !== null) {
			for (const input of this.parentInfo.AllowedInputs) {
				inputs.push(this.fb.control(false));
			}
		}

		const outputs = [];
		if (this.parentInfo.AllowedOutputs !== undefined && this.parentInfo.AllowedOutputs !== null) {
			for (const output of this.parentInfo.AllowedOutputs) {
				outputs.push(this.fb.control(false));
			}
		}

		const device = new Device();
		device.CrpItemId = 1;
		this.addDeviceForm = new FormGroup({
			'selectedModel': new FormControl(device.ModelId, Validators.required),
			'selectedPeriphNumber': new FormControl(device.CrpItemId, Validators.required),
			'name': new FormControl(device.Name, Validators.required),
			'description': new FormControl(device.Description, Validators.required),
			'keyCode': new FormControl('', [Validators.required, Validators.minLength(16),
			Validators.maxLength(16), Validators.pattern('[0-9a-fA-F]+')]),
			'iconName': new FormControl(this.icons[0].value, Validators.required),
			'inputPorts': this.fb.array(inputs),
			'outputPorts': this.fb.array(outputs)
		});
		this.addDeviceForm.addValidators(CustomValidators.multipleCheckboxRequireOneIO('inputPorts', 'outputPorts'));
	}

	subscribeAll(): void {
		this.modelsSub = this.models$
			.pipe(
				map(models => this.supportedModels(models))
			)
			.subscribe(models => {
				this.models = models.filter(model => model.Id < 900);
			}
			);
		this.addSub = this.addedDeviceId$
			.pipe(filter(deviceId => !!deviceId))
			.subscribe(deviceId => {
				this.store.dispatch(SiteActions.getSites({}));
				this.store.dispatch(SiteActions.getSitesOnlineDevices({}));
				this.store.dispatch(DeviceActions.getCurrentDevice({ deviceId }));
				this.dialogRef.close({ deviceId });
			}
			);
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.addDeviceForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.addDeviceForm.get(key).errors;
			if (controlErrors != null) {
				/*Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				});*/
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const categoryId = this.models.find(m => m.Id === +this.f.selectedModel.value.Id).CategoryId;
		const device: Device = {
			SiteId: this.parentDevice.SiteId,
			Name: this.f.name.value,
			CrpItemId: this.f.selectedPeriphNumber.value || null,
			Description: this.f.description.value,
			ParentId: this.parentDevice.Id,
			RootId: this.parentDevice.RootId ? this.parentDevice.RootId : this.parentDevice.Id,
			ModelId: this.f.selectedModel.value.Id,
			IconName: categoryId === EDeviceCategory.Virtual ? this.f.iconName.value : null,
			CategoryId: categoryId,
			Keycode: this.f.keyCode.value,
		};

		if (this.model.Id === EDeviceModel.MultiIO) {
			device.Terminals = this.terminals();
			device.CrpItemId = null;
		}

		this.store.dispatch(DeviceActions.addDevice({ device }));
	}

	get f(): any {
		return this.addDeviceForm.controls;
	}

	get invalidPorts(): boolean {
		if (this.multiIO()) {
			return (!this.anyAllowedInputs() || this.f.inputPorts.invalid) &&
				(!this.anyAllowedOutputs() || this.f.outputPorts.invalid);
		} else {
			return false;
		}
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.modelsSub?.unsubscribe();
		this.addSub?.unsubscribe();
	}

	filterModels(event) {
		const filtered: any[] = [];
		const query = event.query;
		if (this.models) {
			for (let i = 0; i < this.models.length; i++) {
				const models = this.models[i];
				// {{ model.ProductName + ' - ' + ( model.BoardName || '') + ' - ' + model.Description }}
				if (
					(models.ProductName?.toLowerCase().indexOf(query.toLowerCase()) >= 0) ||
					(models.ProductBoardName?.toLowerCase().indexOf(query.toLowerCase()) >= 0) ||
					models.Description?.toLowerCase().indexOf(query.toLowerCase()) >= 0
				) {
					filtered.push(models);
				}
			}
		}
		this.filteredModels = filtered;
	}


	onSelectedModel(): void {
		this.model = this.models.find(m => m.Id === this.f.selectedModel.value.Id);
		this.updateForm();
		this.f.description.setValue(this.model.ProductName);
	}

	updateForm(): void {
		if (this.model.CategoryId === EDeviceCategory.SubGateway) { // subgateway
			this.f.selectedPeriphNumber.disable();
			this.f.iconName.disable();
			this.f.keyCode.enable();
			this.f.inputPorts.disable();
			this.f.outputPorts.disable();
			this.addDeviceForm.removeValidators(CustomValidators.multipleCheckboxRequireOneIO('inputPorts', 'outputPorts'));
		} else if (this.model.CategoryId === EDeviceCategory.Automation) { // automation
			this.periphNumbers = [];
			this.f.keyCode.disable();
			this.f.iconName.disable();
			this.f.inputPorts.disable();
			this.f.outputPorts.disable();
			this.f.selectedPeriphNumber.enable();
			for (let i = 1; i <= 254; i++) {
				this.periphNumbers.push(i);
			}
			this.addDeviceForm.removeValidators(CustomValidators.multipleCheckboxRequireOneIO('inputPorts', 'outputPorts'));
		} else if (this.multiIO()) {
			this.f.selectedPeriphNumber.disable();
			this.f.keyCode.disable();
			this.f.iconName.enable();
			this.f.inputPorts.enable();
			this.f.outputPorts.enable(); this.model.Id === EDeviceModel.MultiIO
			this.addDeviceForm.addValidators(CustomValidators.multipleCheckboxRequireOneIO('inputPorts', 'outputPorts'));
		}
	}

	terminals(): IOPayload[] {
		const terminals: IOPayload[] = [];
		const inputPorts = this.f.inputPorts.value;
		const outputPorts = this.f.outputPorts.value;
		for (let i = 0; i < inputPorts.length; i++) {
			if (inputPorts[i]) {
				terminals.push({
					InputId: this.parentInfo.AllowedInputs[i]
				});
			}
		}
		for (let i = 0; i < outputPorts.length; i++) {
			if (outputPorts[i]) {
				terminals.push({
					OutputId: this.parentInfo.AllowedOutputs[i]
				});
			}
		}

		return terminals;
	}

	multiIO(): boolean {
		return this.model ? this.model.Id === EDeviceModel.MultiIO : false;
	}

	anyAllowedInputs(): boolean {
		return this.parentInfo.AllowedInputs && this.parentInfo.AllowedInputs.length > 0;
	}

	anyAllowedOutputs(): boolean {
		return this.parentInfo.AllowedOutputs && this.parentInfo.AllowedOutputs.length > 0;
	}

	supportedModels(allModels: DeviceModel[]): DeviceModel[] {
		let models: DeviceModel[] = [];
		if ((this.parentInfo.AllowedInputs && this.parentInfo.AllowedInputs.length > 0) ||
			(this.parentInfo.AllowedOutputs && this.parentInfo.AllowedOutputs.length > 0)) {
			models = models.concat(allModels.filter(m => m.Id === EDeviceModel.MultiIO));
		}
		if (this.parentModel && this.parentModel.MaxSubGateways > this.subGatewaysCount
			&& this.parentModel.MaxAutomations > this.automationsCount) {
			const subGateways = allModels.filter(m => m.CategoryId === EDeviceCategory.SubGateway); // Automations + SubGateways
			const automations = allModels.filter(m => m.CategoryId === EDeviceCategory.Automation);
			models = models.concat(subGateways, automations); // puts subgateways at the beginning
		} else if (this.parentModel && this.parentModel.MaxSubGateways > this.subGatewaysCount
			&& this.parentModel.MaxAutomations <= this.automationsCount) {
			models = models.concat(
				allModels.filter(m => m.CategoryId === EDeviceCategory.SubGateway)); // Subgateways only
		} else if (this.parentModel && this.parentModel.MaxSubGateways <= this.subGatewaysCount
			&& this.parentModel.MaxAutomations > this.automationsCount) {
			models = models.concat(
				allModels.filter(m => m.CategoryId === EDeviceCategory.Automation)); // Automations only
		}
		return models;
	}

	convertionModel(model: DeviceModel) {
		return (model.ProductName ? model.ProductName + ' - ' : '') + (model.ProductBoardName ? model.ProductBoardName + ' - ' : '') +
			(model.ProductName ? model.Description : '');
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	newShowErrors(type: AddDeviceDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: AddDeviceDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	showErrorRequiredCheckBox(type: AddDeviceDialogComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}
}